import React, { useRef, useEffect } from "react";
import useNotifications from "../hooks/useNotifications";
import NotificationItem from "./NotificationItem";
import { motion } from "framer-motion";

export default function NotificationDropdown({ onClose }) {
  const { notifications, loading, error, leadWaitingCount, clientWaitingCount } = useNotifications();
  const ref = useRef();

  // Calculate if notifications should be disabled
  const hasWaitingCalls = leadWaitingCount > 0 || clientWaitingCount > 0;

  // Generate the appropriate restriction message
  const getRestrictionMessage = () => {
    if (leadWaitingCount > 0 && clientWaitingCount > 0) {
      return (
        <>
          There is a <strong>lead</strong> and a <strong>client</strong> call already initiated. Please complete them to continue.
        </>
      );
    }
    if (leadWaitingCount > 0) {
      return (
        <>
          There is a <strong>lead</strong> call already initiated. Please complete it to continue.
        </>
      );
    }
    if (clientWaitingCount > 0) {
      return (
        <>
          There is a <strong>client</strong> call already initiated. Please complete it to continue.
        </>
      );
    }
    return null;
  };

  // Prevent background scroll on mobile when dropdown is open
  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, []);

  // Close on outside click
  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [onClose]);

  return (
    <>
      {/* Overlay for mobile modal effect - positioned to not cover header */}
      <div className="fixed top-16 left-0 right-0 bottom-0 bg-black bg-opacity-0 z-40 sm:hidden" onClick={onClose} />
      <motion.div
        ref={ref}
        className="fixed sm:absolute left-0 right-0 sm:right-0 sm:left-auto top-16 sm:top-auto mt-0 sm:mt-3 w-full sm:w-[420px] max-w-full sm:max-w-[420px] bg-white dark:bg-navy-800 backdrop-blur-lg rounded-2xl sm:rounded-3xl shadow-2xl border-t sm:border-t-0 border border-gray-100 dark:border-white/10 z-50 p-0"
        style={{ WebkitOverflowScrolling: 'touch', paddingBottom: 'env(safe-area-inset-bottom)', top: 'calc(100% + 0.1rem)' }}
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -16 }}
        transition={{ duration: 0.22, ease: "easeOut" }}
      >
        <div className="px-4 sm:px-7 pt-5 sm:pt-6 pb-3 border-b border-gray-100 dark:border-white/10">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
            <div>
              <div className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">Notifications</div>
              <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-300 font-medium">Today's Calls</div>
            </div>
            <div className="text-indigo-600 dark:text-indigo-400 font-bold text-sm sm:text-base">
              {`(${notifications.length})`}
            </div>
          </div>
        </div>
        {hasWaitingCalls && (
          <div className="px-4 sm:px-7 py-3 sm:py-4 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-700/30">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                {getRestrictionMessage()}
              </p>
            </div>
          </div>
        )}
        <div className="max-h-[calc(100vh-20rem)] sm:max-h-[420px] overflow-y-auto px-2 sm:px-4 py-3 sm:py-4 space-y-3 sm:space-y-4">
          {loading && (
            <div>
              {[...Array(3)].map((_, i) => (
                <div key={i} className="animate-pulse bg-gray-100 dark:bg-white/10 rounded-2xl h-20 mb-3" />
              ))}
            </div>
          )}
          {error && (
            <div className="text-red-500 dark:text-red-400 text-center py-8">{error}</div>
          )}
          {!loading && notifications.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12">
              <svg className="w-16 h-16 text-gray-200 dark:text-white/10 mb-4" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                <path d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <div className="text-gray-500 dark:text-gray-300 font-medium text-base sm:text-lg">No calls scheduled for today.</div>
            </div>
          )}
          {!loading && notifications.length > 0 && (
            <>
              {notifications.map((n) => (
                <NotificationItem
                  key={n._id}
                  notification={n}
                  isDisabled={hasWaitingCalls}
                />
              ))}
            </>
          )}
        </div>
      </motion.div>
    </>
  );
}
